import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import CreateTableModal from './CreateTableModal';

const HomeInventoryContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [idAzienda, setIdAzienda] = useState(null);
  const [mojeTabele, setMojeTabele] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Realni stats
  const [totalTables, setTotalTables] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const getAzienda = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profil } = await supabase
          .from('profili')
          .select('id_azienda')
          .eq('id', user.id)
          .single();
        if (profil) setIdAzienda(profil.id_azienda);
      }
    };
    getAzienda();
  }, []);

  useEffect(() => {
    const fetchTabele = async () => {
      if (!idAzienda) return;
      setIsLoading(true);
      const { data } = await supabase
        .from('inventura_registar')
        .select('*')
        .eq('id_azienda', idAzienda)
        .order('created_at', { ascending: false });

      if (data) {
        setMojeTabele(data);
        setTotalTables(data.length);
        await fetchRealStats(data);
      }
      setIsLoading(false);
    };
    fetchTabele();
  }, [idAzienda, isModalOpen]);

  // Dohvati realne stats — broji redove iz svake tabele
  const fetchRealStats = async (tabele) => {
    setStatsLoading(true);
    try {
      let totalRowCount = 0;
      let latestDate = null;

      for (const tab of tabele) {
        try {
          // Count redova u svakoj inv_ tabeli
          const { count } = await supabase
            .from(tab.naziv_tabele_sql)
            .select('id', { count: 'exact', head: true });

          if (count) totalRowCount += count;

          // Posljednji zapis iz svake tabele
          const { data: lastRow } = await supabase
            .from(tab.naziv_tabele_sql)
            .select('created_at')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (lastRow?.created_at) {
            const rowDate = new Date(lastRow.created_at);
            if (!latestDate || rowDate > latestDate) latestDate = rowDate;
          }
        } catch {
          // Tabela možda prazna ili ne postoji — preskačemo
        }
      }

      setTotalRows(totalRowCount);
      setLastUpdate(latestDate);
    } catch (err) {
      console.error('Stats fetch error:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatLastUpdate = (date) => {
    if (!date) return '—';
    return date.toLocaleDateString('bs-BA', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const StatCard = ({ icon, iconBg, iconColor, label, value, desc, loading }) => (
    <div className="bg-card p-8 rounded-[2.5rem] border border-border/60 shadow-sm hover:shadow-2xl transition-all duration-500">
      <div className="flex justify-between items-start mb-6">
        <div className={`p-4 ${iconBg} rounded-2xl ${iconColor}`}>
          <Icon name={icon} size={28} />
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground/40">
          {label}
        </span>
      </div>
      {loading ? (
        <div className="h-12 flex items-center">
          <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <h4 className={`text-5xl font-black tracking-tighter mb-1 ${iconColor}`}>{value}</h4>
      )}
      <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">{desc}</p>
    </div>
  );

  return (
    <div className="w-full animate-in fade-in duration-700">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-10">
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-all duration-300"
        >
          <div className="p-2 rounded-xl bg-muted/30 group-hover:bg-primary/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
          </div>
          <span className="text-[11px] font-black uppercase tracking-[0.2em]">{t('inventory.back')}</span>
        </button>

        <button
          onClick={() => setIsModalOpen(true)}
          className="group flex items-center gap-3 px-6 py-3 rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-1 transition-all duration-300 active:scale-95"
        >
          <Icon name="Plus" size={20} className="group-hover:rotate-90 transition-transform duration-500" />
          <span className="text-xs font-black uppercase tracking-widest">{t('inventory.new_table')}</span>
        </button>
      </div>

      {/* NASLOV */}
      <div className="relative mb-12">
        <div className="flex flex-col relative z-10 bg-background pr-4 w-fit">
          <h2 className="text-3xl font-black uppercase tracking-tighter flex items-center gap-4 text-foreground/90">
            <div className="p-2.5 bg-foreground text-background rounded-xl shadow-xl">
              <Icon name="Package" size={24} />
            </div>
            {t('inventory.title')}
          </h2>
          <p className="text-xs text-muted-foreground font-semibold mt-2 opacity-80 tracking-wide italic ml-14">
            {t('inventory.subtitle')}
          </p>
        </div>
        <div className="absolute bottom-[-15px] left-0 w-full h-[1px] bg-gradient-to-r from-border via-border/40 to-transparent" />
      </div>

      {/* REALNE STATISTIČKE KARTICE */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        <StatCard
          icon="Table2"
          iconBg="bg-primary/10"
          iconColor="text-primary"
          label={t('inventory.stats.total')}
          value={totalTables}
          desc={t('inventory.stats.total_desc')}
          loading={isLoading}
        />
  
        <StatCard
          icon="Clock"
          iconBg="bg-blue-500/10"
          iconColor="text-blue-500"
          label={t('inventory.stats.last_update')}
          value={formatLastUpdate(lastUpdate)}
          desc={t('inventory.stats.last_update_desc')}
          loading={statsLoading}
        />
      </div>

      {/* LISTA TABELA */}
      <div className="mb-8 flex items-center gap-3">
        <h3 className="text-sm font-black uppercase tracking-[0.2em] text-foreground/50">
          {t('inventory.active_tables')}
        </h3>
        <div className="h-[1px] flex-1 bg-border/40"></div>
        {!isLoading && (
          <span className="text-[10px] font-black text-muted-foreground/40 uppercase tracking-widest">
            {totalTables} {t('inventory.stats.total_desc')}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="w-full py-20 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : mojeTabele.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mojeTabele.map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(`/dashboard/inventory/table/${tab.naziv_tabele_sql}`)}
              className="group relative bg-card p-8 rounded-[2.5rem] border border-border/60 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 text-left"
            >
              <div className="flex justify-between items-start mb-8">
                <div className="p-4 bg-muted/50 rounded-2xl group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                  <Icon name="Table" size={24} />
                </div>
                <Icon name="ArrowRight" size={20} className="text-muted-foreground/20 group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>

              <h4 className="text-xl font-black uppercase tracking-tight mb-2 group-hover:text-primary transition-colors">
                {tab.naziv_tabele_prikaz}
              </h4>
              <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest opacity-60">
                SQL: {tab.naziv_tabele_sql}
              </p>

              <div className="mt-8 pt-6 border-t border-border/40 flex items-center justify-between">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-foreground">
                  {t('inventory.open_record')}
                </span>
                <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]"></div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="w-full py-20 border-2 border-dashed border-border/40 rounded-[3rem] flex flex-col items-center justify-center text-center px-6">
          <div className="p-6 bg-muted/20 rounded-full mb-6">
            <Icon name="Table" size={40} className="text-muted-foreground/30" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-foreground">{t('inventory.no_tables.title')}</h3>
          <p className="text-sm text-muted-foreground max-w-sm mb-8">{t('inventory.no_tables.desc')}</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-8 py-3 rounded-xl bg-foreground text-background font-black uppercase text-[10px] tracking-[0.2em] hover:opacity-90 transition-all active:scale-95"
          >
            {t('inventory.no_tables.btn')}
          </button>
        </div>
      )}

      <CreateTableModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        idAzienda={idAzienda}
      />
    </div>
  );
};

export default HomeInventoryContent;